import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Wallet, Plus, Pencil, Trash2, Share2, Copy, CheckCircle2, Circle, Loader2, Landmark, ScanLine, FileText, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ACCOUNT_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyAccount = { name: "", bank_id: "", type: "debito", account_number: "", clabe: "", holder: "", currency: "MXN", initial_balance: 0, credit_current_balance: 0, credit_available_balance: 0, cut_date: "", payment_date: "" };
const typeLabel = (t) => ACCOUNT_TYPES.find((x) => x.value === t)?.label || t;

function AccountDialog({ open, onOpenChange, initial, banks }) {
  const qc = useQueryClient();
  const fileRef = React.useRef(null);
  const [form, setForm] = useState(initial || emptyAccount);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  React.useEffect(() => { setForm(initial || emptyAccount); }, [initial, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/accounts/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = res.data.extracted || {};
      const doc = res.data.document;
      setForm((f) => ({
        ...f,
        bank_id: ex.bank_id || f.bank_id,
        account_number: ex.account_number || f.account_number,
        clabe: ex.clabe || f.clabe,
        holder: ex.holder || f.holder,
        type: ex.account_type || f.type,
        name: f.name || (ex.bank_name ? ex.bank_name : f.name),
        document_file_id: doc?.file_id || f.document_file_id,
        document_url: doc?.url || f.document_url,
        document_type: doc?.type || f.document_type,
      }));
      if (ex.error === "no_ocr") toast("Documento guardado (no se pudieron leer los datos)");
      else toast.success("Datos extraídos con IA ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo procesar el documento");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ponle un nombre a la cuenta");
    setSaving(true);
    try {
      const payload = { ...form, initial_balance: Number(form.initial_balance) || 0 };
      if (form.type === "credito") {
        payload.credit_current_balance = Number(form.credit_current_balance) || 0;
        payload.credit_available_balance = Number(form.credit_available_balance) || 0;
        payload.cut_date = form.cut_date || null;
        payload.payment_date = form.payment_date || null;
      }
      if (initial?.id) await api.put(`/accounts/${initial.id}`, payload);
      else await api.post("/accounts", payload);
      toast.success(initial ? "Cuenta actualizada" : "Cuenta creada");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="account-dialog">
        <DialogHeader><DialogTitle>{initial ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle>
          <DialogDescription>Configura el banco, tipo, CLABE y saldo inicial de la cuenta.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" capture="environment" className="hidden" onChange={onScan} data-testid="account-scan-input" />
          <button type="button" data-testid="scan-account-button" onClick={() => fileRef.current?.click()} disabled={scanning}
            className="w-full flex items-center gap-3 rounded-xl border border-dashed border-emerald-500/40 bg-emerald-500/5 px-4 py-3 hover:bg-emerald-500/10 transition-colors">
            {scanning ? <Loader2 className="w-5 h-5 animate-spin text-emerald-500" /> : <ScanLine className="w-5 h-5 text-emerald-500" />}
            <div className="text-left flex-1">
              <p className="text-sm font-medium">Escanear carátula o estado de cuenta</p>
              <p className="text-xs text-muted-foreground">{scanning ? "Leyendo con IA…" : "Foto (JPG/PNG) o PDF · la IA llena banco, cuenta y CLABE"}</p>
            </div>
          </button>
          <div className="space-y-2">
            <Label>Nombre / Alias</Label>
            <Input data-testid="account-name-input" placeholder="Ej. BBVA Nómina, Efectivo cartera" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Banco</Label>
              <Select value={form.bank_id} onValueChange={(v) => set("bank_id", v)}>
                <SelectTrigger data-testid="account-bank-select" className="rounded-xl"><SelectValue placeholder="Banco…" /></SelectTrigger>
                <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />{b.name}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="account-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.type === "credito" ? (
            <div className="space-y-2"><Label>No. de cuenta</Label><Input placeholder="0123456789" value={form.account_number || ""} onChange={(e) => set("account_number", e.target.value)} className="rounded-xl font-numeric" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>No. de cuenta</Label><Input placeholder="0123456789" value={form.account_number || ""} onChange={(e) => set("account_number", e.target.value)} className="rounded-xl font-numeric" /></div>
              <div className="space-y-2"><Label>Saldo inicial</Label><Input type="number" step="0.01" value={form.initial_balance} onChange={(e) => set("initial_balance", e.target.value)} className="rounded-xl font-numeric" /></div>
            </div>
          )}
          {form.type === "credito" && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3.5 space-y-3" data-testid="credit-fields">
              <p className="text-xs font-semibold uppercase tracking-wider text-rose-400">Datos de tarjeta de crédito</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Saldo actual (deuda)</Label><Input data-testid="credit-current-input" type="number" step="0.01" value={form.credit_current_balance} onChange={(e) => set("credit_current_balance", e.target.value)} className="rounded-xl font-numeric" /></div>
                <div className="space-y-2"><Label>Saldo disponible</Label><Input data-testid="credit-available-input" type="number" step="0.01" value={form.credit_available_balance} onChange={(e) => set("credit_available_balance", e.target.value)} className="rounded-xl font-numeric" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Fecha de corte</Label><Input data-testid="credit-cut-input" type="date" value={form.cut_date || ""} onChange={(e) => set("cut_date", e.target.value)} className="rounded-xl font-numeric" /></div>
                <div className="space-y-2"><Label>Fecha de pago</Label><Input data-testid="credit-payment-input" type="date" value={form.payment_date || ""} onChange={(e) => set("payment_date", e.target.value)} className="rounded-xl font-numeric" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Al llegar la fecha de corte se generará un pago en Próximos Pagos con el saldo actual y la fecha de pago como límite.</p>
            </div>
          )}
          <div className="space-y-2"><Label>CLABE (18 dígitos)</Label><Input placeholder="012180001234567890" value={form.clabe || ""} onChange={(e) => set("clabe", e.target.value)} className="rounded-xl font-numeric" /></div>
          <div className="space-y-2"><Label>Titular</Label><Input placeholder="Nombre del titular" value={form.holder || ""} onChange={(e) => set("holder", e.target.value)} className="rounded-xl" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancelar</Button>
          <Button data-testid="save-account-button" onClick={save} disabled={saving} className="rounded-xl">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ account, onClose }) {
  const copy = (label, val) => { navigator.clipboard?.writeText(val || ""); toast.success(`${label} copiada`); };
  const shareText = `Datos para transferencia:\nTitular: ${account?.holder || "-"}\nBanco: ${account?.bank?.name || "-"}\nCuenta: ${account?.account_number || "-"}\nCLABE: ${account?.clabe || "-"}`;
  const shareAll = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Datos de transferencia", text: shareText }); } catch {} }
    else { navigator.clipboard?.writeText(shareText); toast.success("Datos copiados"); }
  };
  const Row = ({ label, val }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0"><p className="text-xs text-muted-foreground">{label}</p><p className="font-numeric text-sm truncate">{val || "—"}</p></div>
      {val && <button onClick={() => copy(label, val)} className="text-muted-foreground hover:text-emerald-500 shrink-0"><Copy className="w-4 h-4" /></button>}
    </div>
  );
  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader><DialogTitle>Compartir datos · {account?.name}</DialogTitle>
          <DialogDescription>Copia o comparte los datos para recibir transferencias.</DialogDescription></DialogHeader>
        <div className="rounded-xl border border-border bg-secondary/40 px-4">
          <Row label="Titular" val={account?.holder} />
          <Row label="Banco" val={account?.bank?.name} />
          <Row label="No. de cuenta" val={account?.account_number} />
          <Row label="CLABE" val={account?.clabe} />
        </div>
        <Button onClick={shareAll} className="rounded-xl gap-2 w-full"><Share2 className="w-4 h-4" /> Compartir todo</Button>
      </DialogContent>
    </Dialog>
  );
}

function ReconcileDialog({ account, onClose }) {
  const qc = useQueryClient();
  const { format } = useCurrency();
  const { data = [], isLoading } = useQuery({
    queryKey: ["account-movements", account?.id],
    queryFn: async () => (await api.get(`/accounts/${account.id}/movements`)).data,
    enabled: !!account,
  });
  const toggle = async (m) => {
    await api.post(`/movements/${m.id}/reconcile`, { reconciled: !m.reconciled });
    qc.invalidateQueries({ queryKey: ["account-movements", account.id] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
  };
  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog">
        <DialogHeader><DialogTitle>Conciliación · {account?.name}</DialogTitle>
          <DialogDescription>Marca los movimientos que ya aparecen en tu estado de cuenta.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground">Saldo en libros</p><p className="font-numeric font-bold text-lg">{format(account?.book_balance)}</p></div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3"><p className="text-xs text-muted-foreground">Saldo conciliado</p><p className="font-numeric font-bold text-lg text-emerald-500">{format(account?.reconciled_balance)}</p></div>
        </div>
        {isLoading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : data.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Sin movimientos en esta cuenta.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.map((m) => (
              <button key={m.id} onClick={() => toggle(m)} data-testid={`reconcile-toggle-${m.id}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-accent/50 rounded-lg px-2">
                {m.reconciled ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
                <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{m.category}</p><p className="text-xs text-muted-foreground font-numeric">{m.movement_date}</p></div>
                <span className={`font-numeric text-sm font-semibold ${m.type === "by" ? "text-emerald-500" : "text-rose-500"}`}>{m.type === "by" ? "+" : "−"}{format(m.amount)}</span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Accounts() {
  const qc = useQueryClient();
  const { format } = useCurrency();
  const { data: accounts = [], isLoading } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });
  const { data: banks = [] } = useQuery({ queryKey: ["banks"], queryFn: async () => (await api.get("/catalog/banks")).data });
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [share, setShare] = useState(null);
  const [reconcile, setReconcile] = useState(null);

  const total = accounts.filter((a) => a.type !== "credito").reduce((s, a) => s + (a.book_balance || 0), 0);

  const del = async (a) => {
    if (!window.confirm(`¿Eliminar la cuenta "${a.name}"?`)) return;
    try { await api.delete(`/accounts/${a.id}`); toast.success("Cuenta eliminada"); qc.invalidateQueries(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div className="py-4" data-testid="accounts-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Wallet className="w-6 h-6 text-emerald-500" /><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Cuentas</h1></div>
          <p className="text-muted-foreground text-sm mt-1">Administra tus cuentas, consulta CLABE y concilia saldos.</p>
        </div>
        <Button data-testid="add-account-button" onClick={() => setDialog({ open: true, initial: null })} className="rounded-xl gap-2"><Plus className="w-4 h-4" /> Nueva cuenta</Button>
      </div>

      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/40 p-6 my-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Saldo total en cuentas</p>
        <p className="font-numeric font-bold text-3xl sm:text-4xl mt-2">{format(total)}</p>
        <p className="text-xs text-muted-foreground mt-1">No incluye tarjetas de crédito (deuda).</p>
      </div>

      {isLoading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="accounts-list">
          {accounts.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: (a.bank?.color || "#64748b") + "22" }}>
                    <Landmark className="w-5 h-5" style={{ color: a.bank?.color || "#64748b" }} />
                  </div>
                  <div><p className="font-semibold">{a.name}</p><p className="text-xs text-muted-foreground">{a.bank?.name || "—"} · {typeLabel(a.type)}</p></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setShare(a)} data-testid={`share-account-${a.id}`} className="w-8 h-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-emerald-500"><Share2 className="w-4 h-4" /></button>
                  <button onClick={() => setDialog({ open: true, initial: a })} data-testid={`edit-account-${a.id}`} className="w-8 h-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(a)} className="w-8 h-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-muted-foreground hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {a.type === "credito" ? (
                <div className="mt-4 space-y-3" data-testid={`credit-preview-${a.id}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
                      <p className="text-xs text-muted-foreground">Saldo actual</p>
                      <p className="font-numeric font-bold text-xl text-rose-500" data-testid={`credit-current-${a.id}`}>{format(a.credit_current_balance || 0)}</p>
                    </div>
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                      <p className="text-xs text-muted-foreground">Disponible</p>
                      <p className="font-numeric font-bold text-xl text-emerald-500" data-testid={`credit-available-${a.id}`}>{format(a.credit_available_balance || 0)}</p>
                    </div>
                  </div>
                  {(a.cut_date || a.payment_date) && (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground font-numeric">
                      {a.cut_date && <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Corte: {a.cut_date}</span>}
                      {a.payment_date && <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-rose-400" /> Pago: {a.payment_date}</span>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 flex items-end justify-between">
                  <div><p className="text-xs text-muted-foreground">Saldo actual</p><p className="font-numeric font-bold text-2xl">{format(a.book_balance)}</p></div>
                  <button onClick={() => setReconcile(a)} data-testid={`reconcile-account-${a.id}`} className="text-xs font-medium text-emerald-500 flex items-center gap-1 hover:underline"><ScanLine className="w-3.5 h-3.5" /> Conciliar</button>
                </div>
              )}
              {a.clabe && <p className="text-xs text-muted-foreground font-numeric mt-3 truncate">CLABE: {a.clabe}</p>}
              {a.document_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${a.document_url}`} target="_blank" rel="noreferrer"
                  data-testid={`view-document-${a.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-500 hover:underline">
                  <FileText className="w-3.5 h-3.5" /> Ver carátula / estado de cuenta
                </a>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <AccountDialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, initial: o ? dialog.initial : null })} initial={dialog.initial} banks={banks} />
      <ShareDialog account={share} onClose={() => setShare(null)} />
      <ReconcileDialog account={reconcile} onClose={() => setReconcile(null)} />
    </div>
  );
}
